import { queryOptions, useQuery } from '@tanstack/react-query';
import type { AdminPlaceDto, AdminPlaceListQuery, PlaceMatchResponse } from '@upme/contracts';
import { api } from '../../../app/api-client';

export const placesQueryKey = (q: Partial<AdminPlaceListQuery>) => ['admin', 'places', q] as const;

export function useAdminPlaces(query: Partial<AdminPlaceListQuery>, opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: placesQueryKey(query),
    enabled: opts?.enabled !== false,
    queryFn: async () => {
      const params = new URLSearchParams();
      for (const [k, v] of Object.entries(query)) {
        if (v != null) params.set(k, String(v));
      }
      return api
        .get('admin/places', { searchParams: params })
        .json<{ items: AdminPlaceDto[]; nextCursor: string | null }>();
    },
  });
}

export const placeByIdOptions = (id: string) =>
  queryOptions({
    queryKey: ['admin', 'place', id] as const,
    queryFn: () => api.get(`admin/places/${id}`).json<AdminPlaceDto>(),
  });

export function useAdminPlace(id: string | undefined, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['admin', 'place', id] as const,
    queryFn: () => api.get(`admin/places/${id}`).json<AdminPlaceDto>(),
    enabled: options?.enabled !== false && !!id,
  });
}

export function usePlaceMatchQuery(args: { cityId: string; q: string; enabled?: boolean }) {
  const q = args.q.trim();
  return useQuery({
    queryKey: ['admin', 'places', 'match', args.cityId, q] as const,
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams();
      params.set('cityId', args.cityId);
      params.set('q', q);
      return api.get('admin/places/match', { searchParams: params, signal }).json<PlaceMatchResponse>();
    },
    enabled: (args.enabled ?? true) && q.length >= 3 && Boolean(args.cityId),
    staleTime: 30_000,
  });
}
